import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TfiReload } from 'react-icons/tfi';
import useWindowSize from 'react-use/lib/useWindowSize';
import Confetti from 'react-confetti';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
//////////////////////////////////////////////////////////////////
import { CollectionsService } from '@/core/services';
import { useCustomDispatch } from '@/core/redux/hooks';
import {
	// setActiveCollection,
	setCompleteCollection,
	setWonSticker,
	updateCollection,
} from '@/core/redux/slices';
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogOverlay,
	DialogTitle,
} from '@/app/shared/components';

import './GifWon.css';
import { ClaimGift } from '@/core/interfaces';

interface GiftWonProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GiftWon: React.FC<GiftWonProps> = ({ isOpen, onClose }) => {
	/*********************
	// #region Hooks
	**********************/
	const { t } = useTranslation();
	const dispatch = useCustomDispatch();
	const { width, height } = useWindowSize();
	const navigate = useNavigate();

	/*********************
	// #region States
	**********************/
	const [showInitialContent, setShowInitialContent] = useState(true);
	const [showGiftContent, setShowGiftContent] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [showConfetti, setShowConfetti] = useState(true);
	const [sticker, setSticker] = useState<ClaimGift>({
		collection: '',
		collectionTitle: '',
		isCollectionCompleted: false,
		isStickerRepeated: false,
		sticker: {
			position: 1,
			image: '',
		},
	});

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		if (showInitialContent && sticker.collection === '') {
			setShowGiftContent(true);
		}
	}, [showInitialContent, sticker]);

	/*********************
	// #region Functions
	**********************/
	const claimGift = async () => {
		setIsLoading(true);
		try {
			const response = await CollectionsService.claimGift();
			setSticker(response);
			setShowConfetti(false);
			setShowInitialContent(false);
		} catch (error) {
			toast.error('Error claiming the sticker');
		}
		setIsLoading(false);
	};

	const handleNewSticker = (
		isCollectionCompleted: boolean,
		isStickerRepeated: boolean
	) => {
		if (!sticker) return;
		dispatch(setWonSticker(null));
		if (!isStickerRepeated) {
			dispatch(
				updateCollection({
					collectionID: sticker.collection,
					sticker: sticker.sticker,
				})
			);
			if (isCollectionCompleted) {
				dispatch(
					setCompleteCollection({
						collectionID: sticker.collection,
					})
				);
			}
		}
		onClose(false);

		if (
			(!sticker.isCollectionCompleted && !sticker.isStickerRepeated) ||
			(sticker.isCollectionCompleted && !sticker.isStickerRepeated)
		) {
			navigate('/collections');
		}
	};

	const renderContent = () => {
		if (showInitialContent) {
			return (
				<div
					className={`flex items-center space-x-2 transition-opacity duration-1000 ease-in-out ${
						!showInitialContent
							? 'animate-out fade-out zoom-out fill-mode-forwards hidden'
							: ''
					}`}
				>
					<div className="grid gap-2 w-full justify-center">
						<p className="text-xl text-center font-bold">
							{t('general.giftWon')}
						</p>
						<img
							className="mx-auto my-6 max-w-[200px]"
							src={process.env.VITE_COLLECTION_GIFT_IMAGE}
							alt="Gift"
						/>
						<Button
							type="button"
							variant="primary"
							onClick={claimGift}
							className="py-6 w-full flex items-center gap-1 text-sm rounded-lg z-200"
							disabled={isLoading}
						>
							{isLoading ? (
								<div className="mr-2 h-4 w-4 animate-spin">
									<TfiReload />
								</div>
							) : (
								t('general.claim')
							)}
						</Button>
					</div>
				</div>
			);
		}

		if (showGiftContent && sticker) {
			return (
				<div
					className={`flex items-center space-x-2 transition-opacity duration-1000 ease-in-out ${
						showGiftContent && !showInitialContent
							? 'animate-in fade-in zoom-in fill-mode-forwards'
							: 'hidden'
					}`}
				>
					<div className="grid flex-1 gap-2 w-full justify-center outline-none">
						<p className="text-xl text-center font-bold z-50">
							{sticker.isStickerRepeated && (
								<>
									{t('collections.stickerRepeated')}{' '}
									<span className="text-yellow-400">
										{sticker.collectionTitle}
									</span>
								</>
							)}

							{sticker.isCollectionCompleted &&
								!sticker.isStickerRepeated && (
									<>
										{t(
											'collections.collectionCompletedOne'
										)}{' '}
										<span className="text-yellow-400">
											{sticker.collectionTitle}
										</span>{' '}
										{t(
											'collections.collectionCompletedTwo'
										)}
									</>
								)}

							{!sticker.isCollectionCompleted &&
								!sticker.isStickerRepeated && (
									<>
										{t('collections.stickerNew')}{' '}
										<span className="text-yellow-400">
											{sticker.collectionTitle}
										</span>
									</>
								)}
						</p>
						{sticker.sticker.image && (
							<img
								className="mx-auto my-20 max-w-[200px] border-2 border-yellow-400 animate-zoom"
								src={sticker.sticker.image}
								alt="Sticker"
							/>
						)}
						<DialogClose asChild>
							<Button
								type="button"
								variant="secondary"
								onClick={() =>
									handleNewSticker(
										sticker.isCollectionCompleted,
										sticker.isStickerRepeated
									)
								}
							>
								{sticker.isStickerRepeated &&
									t('collections.btn_repeated')}
								{sticker.isCollectionCompleted &&
									!sticker.isStickerRepeated &&
									t('general.close')}
								{!sticker.isCollectionCompleted &&
									!sticker.isStickerRepeated &&
									t('collections.btn_add_collection')}
							</Button>
						</DialogClose>
					</div>
				</div>
			);
		}

		return null;
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogOverlay className="bg-customer-950/70" />
			<DialogContent
				className="border-none bg-transparent"
				onInteractOutside={(event) => event.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle>
						<VisuallyHidden.Root>
							{t('general.giftWon')}
						</VisuallyHidden.Root>
					</DialogTitle>
					<DialogDescription className="text-lg text-slate-100 p-0 mt-0 m-0 leading-1 space-y-0">
						<VisuallyHidden.Root>
							{t('general.giftWon')}
						</VisuallyHidden.Root>
					</DialogDescription>
				</DialogHeader>
				{renderContent()}
			</DialogContent>
			{showConfetti && (
				<>
					testconfetti
					<Confetti
						width={width}
						height={height}
						className="z-200 !fixed top-0 left-0"
					/>
				</>
			)}
		</Dialog>
	);
};
